import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Star, MapPin, Phone, Mail, Globe, Clock, MessageSquare, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function BusinessProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [reviewForm, setReviewForm] = useState({ user_name: '', user_email: '', rating: 5, comment: '' });

  useEffect(() => {
    fetchBusinessData();
  }, [id]);

  const fetchBusinessData = async () => {
    setLoading(true);
    try {
      const [businessRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/businesses/${id}`),
        axios.get(`${API}/reviews/${id}`),
      ]);
      setBusiness(businessRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Failed to fetch business data:', error);
      toast.error('Failed to load business details');
    } finally {
      setLoading(false);
    }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads`, {
        business_id: id,
        ...leadForm,
      });
      toast.success('Your inquiry has been sent!');
      setLeadDialogOpen(false);
      setLeadForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send inquiry');
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reviews`, {
        business_id: id,
        ...reviewForm,
      });
      toast.success('Review submitted successfully!');
      setReviewDialogOpen(false);
      setReviewForm({ user_name: '', user_email: '', rating: 5, comment: '' });
      fetchBusinessData();
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  const handleClaimBusiness = async () => {
    if (!user) {
      toast.error('Please login to claim this business');
      navigate('/?auth=login');
      return;
    }

    try {
      await axios.post(`${API}/businesses/${id}/claim`);
      toast.success('Claim request submitted! Admin will review shortly.');
      setClaimDialogOpen(false);
      fetchBusinessData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim request');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container mx-auto px-4 md:px-8 py-20 text-center">
          <p className="text-lg text-slate-600">Business not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Hero Image */}
        <div
          className="h-96 rounded-2xl bg-cover bg-center mb-8 relative overflow-hidden"
          style={{
            backgroundImage: `url(${business.logo || 'https://images.unsplash.com/photo-1646602201694-43d9acc2b542?w=1200'})`,
          }}
          data-testid="business-hero-image"
        >
          {business.is_featured && (
            <div className="absolute top-6 right-6">
              <span className="bg-accent text-white px-4 py-2 rounded-full text-sm font-medium">
                Featured Business
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Business Info */}
            <Card className="p-8 shadow-sm" data-testid="business-info-card">
              <h1 className="text-4xl font-bold mb-4" data-testid="business-name">
                {business.name}
              </h1>

              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.floor(business.rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-slate-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-lg font-medium" data-testid="business-rating">
                  {business.rating || 0} ({business.review_count || 0} reviews)
                </span>
              </div>

              <p className="text-lg text-slate-700 leading-relaxed mb-6" data-testid="business-description">
                {business.description}
              </p>

              {business.opening_hours && (
                <div className="flex items-start gap-2 text-slate-600">
                  <Clock className="w-5 h-5 mt-0.5" />
                  <div>
                    <p className="font-medium">Opening Hours</p>
                    <p>{business.opening_hours}</p>
                  </div>
                </div>
              )}
            </Card>

            {/* Reviews */}
            <Card className="p-8 shadow-sm" data-testid="reviews-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold">Reviews</h2>
                <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="rounded-full bg-primary hover:bg-primary/90"
                      data-testid="write-review-button"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Write a Review
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Write a Review</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleReviewSubmit} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Your Name</Label>
                        <Input
                          required
                          value={reviewForm.user_name}
                          onChange={(e) => setReviewForm({ ...reviewForm, user_name: e.target.value })}
                          data-testid="review-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          required
                          value={reviewForm.user_email}
                          onChange={(e) => setReviewForm({ ...reviewForm, user_email: e.target.value })}
                          data-testid="review-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rating</Label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                              data-testid={`star-rating-${star}`}
                            >
                              <Star
                                className={`w-8 h-8 cursor-pointer ${
                                  star <= reviewForm.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-slate-300'
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Comment</Label>
                        <Textarea
                          required
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                          rows={4}
                          data-testid="review-comment-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full rounded-full bg-primary hover:bg-primary/90"
                        data-testid="submit-review-button"
                      >
                        Submit Review
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {reviews.length === 0 ? (
                <p className="text-slate-600" data-testid="no-reviews">No reviews yet. Be the first to review!</p>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-slate-200 pb-6 last:border-0" data-testid={`review-${review.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold">{review.user_name}</p>
                          <div className="flex items-center gap-1 mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-slate-700">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Claim Business Card */}
            {!business.is_claimed && (
              <Card className="p-6 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100" data-testid="claim-card">
                <div className="flex items-start gap-3 mb-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-blue-900">Is this your business?</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Claim this listing to manage it and respond to customer inquiries
                    </p>
                  </div>
                </div>

                <Dialog open={claimDialogOpen} onOpenChange={setClaimDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white mt-3"
                      data-testid="claim-business-button"
                    >
                      Claim This Business
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Claim Business Listing</DialogTitle>
                      <DialogDescription className="text-sm text-slate-600 mt-2">
                        By claiming this business, you'll be able to:
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>Update business information</li>
                          <li>Respond to customer reviews</li>
                          <li>Manage customer inquiries</li>
                          <li>Add photos and details</li>
                        </ul>
                      </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4">
                      <p className="text-sm text-slate-600 mb-4">
                        Your claim request will be reviewed by our admin team. You'll be notified once approved.
                      </p>
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={() => setClaimDialogOpen(false)}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleClaimBusiness}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="confirm-claim-button"
                        >
                          Submit Claim Request
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </Card>
            )}

            {/* Contact Card */}
            <Card className="p-6 shadow-sm" data-testid="contact-card">
              <h3 className="text-xl font-semibold mb-4">Contact Information</h3>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-slate-500">Address</p>
                    <p className="text-slate-900" data-testid="business-address">{business.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-slate-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-slate-500">Phone</p>
                    <a
                      href={`tel:${business.phone}`}
                      className="text-primary hover:underline"
                      data-testid="business-phone"
                    >
                      {business.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-slate-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm text-slate-500">Email</p>
                    <a
                      href={`mailto:${business.email}`}
                      className="text-primary hover:underline break-all"
                      data-testid="business-email"
                    >
                      {business.email}
                    </a>
                  </div>
                </div>

                {business.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-slate-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm text-slate-500">Website</p>
                      <a
                        href={business.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                        data-testid="business-website"
                      >
                        {business.website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Get Quote Card */}
            <Card className="p-6 shadow-sm bg-gradient-to-br from-emerald-50 to-orange-50" data-testid="quote-card">
              <h3 className="text-xl font-semibold mb-2">Interested?</h3>
              <p className="text-slate-600 mb-4">Send an inquiry to this business</p>

              <Dialog open={leadDialogOpen} onOpenChange={setLeadDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="w-full rounded-full bg-accent hover:bg-accent/90 text-white"
                    data-testid="get-quote-button"
                  >
                    Get Quote
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Send Inquiry</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleLeadSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Your Name</Label>
                      <Input
                        required
                        value={leadForm.name}
                        onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                        data-testid="lead-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        required
                        value={leadForm.email}
                        onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                        data-testid="lead-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        type="tel"
                        required
                        value={leadForm.phone}
                        onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                        data-testid="lead-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Message</Label>
                      <Textarea
                        required
                        value={leadForm.message}
                        onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })}
                        rows={4}
                        data-testid="lead-message-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full rounded-full bg-primary hover:bg-primary/90"
                      data-testid="submit-lead-button"
                    >
                      Send Inquiry
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
